/**
 * AI-OS Design-System core primitives — Slice 8, Gate 2 (frozen ט.3).
 *
 * Every component here obeys the four framework laws:
 *  - Data-First: a component renders REAL seam data or its honest empty
 *    state — never a placeholder, never demo data (ט.4).
 *  - Tokens only: colors/spacing/radii come from the --aios-* CSS variables
 *    set on the shell root (src/aios/tokens.js). No hard-coded colors.
 *  - Direction-agnostic: logical CSS properties only (inline-start/end) —
 *    the app flips RTL/LTR at runtime for its 5 languages.
 *  - Everyday-enterprise a11y (י״א.15): visible text (nothing hover-only),
 *    real loading states only, every failure has a Retry.
 */
import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Box, Typography, Button, CircularProgress } from "@material-ui/core";
import { aiosT } from "../strings";

const useStyles = makeStyles(() => ({
  card: {
    borderRadius: "var(--aios-radius-xl)",
    border: "1px solid var(--aios-border)",
    backgroundColor: "var(--aios-surface1)",
    boxShadow: "var(--aios-shadow-card)",
    padding: "var(--aios-space-lg)",
    display: "flex",
    flexDirection: "column",
    gap: "var(--aios-space-sm)",
    minWidth: 0,
  },
  cardHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "var(--aios-space-sm)",
    flexWrap: "wrap",
  },
  cardTitle: {
    fontSize: 15,
    fontWeight: 700,
    letterSpacing: -0.2,
    color: "var(--aios-text-primary)",
  },
  cardSubtitle: {
    fontSize: 12.5,
    color: "var(--aios-text-secondary)",
    marginBlockStart: 2,
  },
  cardFootnote: {
    fontSize: 12,
    color: "var(--aios-text-muted)",
    borderBlockStart: "1px solid var(--aios-border)",
    paddingBlockStart: "var(--aios-space-xs)",
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: 0.3,
    textTransform: "uppercase",
    color: "var(--aios-text-secondary)",
    marginBlockEnd: "var(--aios-space-xs)",
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    borderRadius: 999,
    paddingBlock: 2,
    paddingInline: 10,
    fontSize: 12,
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  badgeOk: {
    color: "var(--aios-brand-primary-strong)",
    backgroundColor: "var(--aios-brand-tint)",
  },
  badgeWarn: {
    color: "var(--aios-warning)",
    backgroundColor: "var(--aios-warning-tint)",
  },
  badgeError: {
    color: "var(--aios-error)",
    backgroundColor: "var(--aios-error-tint)",
  },
  badgeMuted: {
    color: "var(--aios-text-secondary)",
    backgroundColor: "var(--aios-surface2)",
  },
  badgeBrand: {
    color: "var(--aios-text-on-brand)",
    background: "var(--aios-brand-gradient)",
  },
  kvRow: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: "var(--aios-space-sm)",
    paddingBlock: 4,
    borderBlockEnd: "1px dashed var(--aios-border)",
    "&:last-child": { borderBlockEnd: "none" },
  },
  kvLabel: {
    fontSize: 13,
    color: "var(--aios-text-secondary)",
    flexShrink: 0,
  },
  kvValue: {
    fontSize: 13.5,
    fontWeight: 600,
    color: "var(--aios-text-primary)",
    textAlign: "end",
    overflowWrap: "anywhere",
    minWidth: 0,
  },
  kvHint: {
    fontSize: 11.5,
    color: "var(--aios-text-muted)",
    marginBlockStart: 2,
  },
  empty: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    gap: "var(--aios-space-xs)",
    borderRadius: "var(--aios-radius-lg)",
    border: "1px dashed var(--aios-border-input)",
    padding: "var(--aios-space-lg)",
    backgroundColor: "var(--aios-surface2)",
  },
  emptyTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: "var(--aios-text-primary)",
  },
  emptyReason: {
    fontSize: 13,
    color: "var(--aios-text-secondary)",
    maxWidth: 460,
  },
  emptyHint: {
    fontSize: 12,
    color: "var(--aios-text-muted)",
    maxWidth: 460,
  },
  loadWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "var(--aios-space-sm)",
    padding: "var(--aios-space-xl)",
    textAlign: "center",
    // Span the whole panel grid when this panel is itself a grid child; inert
    // everywhere else, so no existing call site changes.
    gridColumn: "1 / -1",
  },
  loadText: {
    fontSize: 13,
    color: "var(--aios-text-secondary)",
  },
  noteBanner: {
    borderRadius: "var(--aios-radius-md)",
    border: "1px solid var(--aios-border)",
    backgroundColor: "var(--aios-surface2)",
    padding: "var(--aios-space-sm) var(--aios-space-md)",
    fontSize: 12.5,
    color: "var(--aios-text-secondary)",
    lineHeight: 1.5,
  },
}));

/** Surface card: title / optional subtitle / optional action / body / footnote. */
export function AiosCard({ title, subtitle, action, footnote, children }) {
  const classes = useStyles();
  return (
    <Box className={classes.card} component="section">
      {(title || action) && (
        <Box className={classes.cardHeader}>
          <Box minWidth={0}>
            {title && <Typography className={classes.cardTitle}>{title}</Typography>}
            {subtitle && (
              <Typography className={classes.cardSubtitle}>{subtitle}</Typography>
            )}
          </Box>
          {action || null}
        </Box>
      )}
      {children}
      {footnote && <Typography className={classes.cardFootnote}>{footnote}</Typography>}
    </Box>
  );
}

export function SectionTitle({ children }) {
  const classes = useStyles();
  return <Typography className={classes.sectionTitle}>{children}</Typography>;
}

/** Status chip. variant: ok | warn | error | muted | brand. Text always visible. */
export function StatBadge({ variant = "muted", children }) {
  const classes = useStyles();
  const variantClass = {
    ok: classes.badgeOk,
    warn: classes.badgeWarn,
    error: classes.badgeError,
    muted: classes.badgeMuted,
    brand: classes.badgeBrand,
  }[variant] || classes.badgeMuted;
  return <span className={`${classes.badge} ${variantClass}`}>{children}</span>;
}

/** Label/value row with optional muted hint below the value. */
export function KeyValueRow({ label, value, hint }) {
  const classes = useStyles();
  return (
    <Box>
      <Box className={classes.kvRow}>
        <Typography className={classes.kvLabel}>{label}</Typography>
        <Typography className={classes.kvValue} component="div">
          {value}
        </Typography>
      </Box>
      {hint && <Typography className={classes.kvHint}>{hint}</Typography>}
    </Box>
  );
}

/**
 * Honest empty state (ט.4): says what is missing, WHY, and what will open
 * the data — never fakes, never promises.
 */
export function EmptyState({ icon: Icon, title, reason, hint }) {
  const classes = useStyles();
  return (
    <Box className={classes.empty} role="status">
      {Icon && <Icon style={{ fontSize: 34, color: "var(--aios-text-muted)" }} />}
      {title && <Typography className={classes.emptyTitle}>{title}</Typography>}
      {reason && <Typography className={classes.emptyReason}>{reason}</Typography>}
      {hint && <Typography className={classes.emptyHint}>{hint}</Typography>}
    </Box>
  );
}

/**
 * Loading/error/content wrapper. The spinner shows only while data is
 * ACTUALLY being fetched (no skeleton theater); every failure renders a
 * visible Retry (י״א.15).
 */
/**
 * `loadWrap` carries `gridColumn: "1 / -1"` (see useStyles): a panel body is now
 * ONE auto-fit grid, and where a LoadPanel is itself a grid child its spinner
 * would otherwise sit in a single ~290px column with empty track beside it.
 * The property is inert wherever the parent is not a grid, so every other call
 * site is unaffected.
 */
export function LoadPanel({ loading, error, onRetry, children }) {
  const classes = useStyles();
  if (loading) {
    return (
      <Box className={classes.loadWrap} role="status" aria-live="polite">
        <CircularProgress size={26} style={{ color: "var(--aios-brand-primary-strong)" }} />
        <Typography className={classes.loadText}>{aiosT("loading")}</Typography>
      </Box>
    );
  }
  if (error) {
    return (
      <Box className={classes.loadWrap} role="alert">
        <Typography className={classes.loadText}>{aiosT("loadError")}</Typography>
        {onRetry && (
          <Button variant="outlined" size="small" onClick={onRetry}>
            {aiosT("retry")}
          </Button>
        )}
      </Box>
    );
  }
  return children || null;
}

/** Informational banner (honesty notes: shadow mode, deferred inputs, …). */
export function NoteBanner({ children }) {
  const classes = useStyles();
  return (
    <Box className={classes.noteBanner} role="note">
      {children}
    </Box>
  );
}
